/**
 * JWT Helper utilities
 * 
 * Simple JWT parsing and validation without external dependencies
 */

/**
 * Parse a JWT token without verification
 * This is safe for checking expiration since we only trust verified tokens from Logto
 */
export function parseJWT(token: string): { exp?: number; iat?: number; [key: string]: unknown } | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }
    
    // Decode the payload (middle part)
    const payload = parts[1];
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded);
  } catch (error) {
    console.error('Failed to parse JWT:', error);
    return null;
  }
}

/**
 * Check if a JWT token is expired
 * Returns true if expired, false if still valid, null if cannot determine
 */
export function isTokenExpired(token: string): boolean {
  const payload = parseJWT(token);
  if (!payload || !payload.exp) {
    // Cannot determine expiration - assume expired for safety
    return true;
  }
  
  // Check if token is expired (with 30 second buffer)
  const now = Math.floor(Date.now() / 1000);
  const expiresAt = payload.exp;
  const bufferSeconds = 30;
  
  return now >= (expiresAt - bufferSeconds);
}

/**
 * Get the expiration time of a JWT token
 */
export function getTokenExpiration(token: string): Date | null {
  const payload = parseJWT(token);
  if (!payload || !payload.exp) {
    return null;
  }
  
  return new Date(payload.exp * 1000);
}
